import {Injectable, inject} from '@angular/core';

import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, take} from 'rxjs/operators';
import {Router} from '@angular/router';
import {
    HttpErrorResponse,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import {UserService} from '../services/user.service';
import {JwtService, FwTokenType} from '../services/jwt.service';
import {ToastrService} from 'ngx-toastr';
import {myAppConfig} from '../../settings';
import {environment} from '../../../environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private userService = inject(UserService);
    private jwtService = inject(JwtService);
    private router = inject(Router);
    private toastrService = inject(ToastrService);

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | any> {

        if (request.url.startsWith('./assets/')) {
            return next.handle(request);
        }

        if (
            myAppConfig.auth.interceptorWhitelist.some(element => {
                return request.url.startsWith(element);
            })
        ) {
            return next.handle(request);
        }

        if (request.body && request.body.action !== 'framework.Auth/newAnonymous') {
            return next.handle(this.addTokenToRequest(request, this.jwtService.getToken(FwTokenType.ACCESS_TOKEN))).pipe(
                map((response: any) => {
                    if (response.body && response.body.databaseError) {
                        this.router.navigate(['db-error']);
                    }
                    return response;
                }),
                // @ts-ignore
                catchError(err => {
                    if (err instanceof HttpErrorResponse) {
                        switch ((err as HttpErrorResponse).status) {
                            case 401:
                                return this.handle401Error(request, next);
                            case 500:
                                this.toastrService.error('Internal Error');
                                // this.router.navigate([this.settingsService.frameworkSettings.auth.afterLogoutDestination]);
                                /*if (environment.production) {
                                    return this.userService.logout();
                                }*/
                            // return of(err);
                        }
                    } else {
                        return throwError(err);
                    }
                })
            );
        } else {
            if (this.jwtService.getToken(FwTokenType.ACCESS_TOKEN)) {
                return next.handle(this.addTokenToRequest(request, this.jwtService.getToken(FwTokenType.ACCESS_TOKEN))).pipe(
                    map((response: any) => {
                        if (response.body && response.body.databaseError) {
                            this.router.navigate(['db-error']);
                        }
                        return response;
                    })
                );
            } else {
                return next.handle(request).pipe(
                    map((response: any) => {
                        if (response.body && response.body.databaseError) {
                            this.router.navigate(['db-error']);
                        }
                        return response;
                    })
                );
            }
        }
    }

    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({setHeaders: {Authorization: `Bearer ${token}`}});
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return this.jwtService.refreshToken().pipe(
                // @ts-ignore
                switchMap((response: any) => {
                    if (response) {
                        if (response.jwt_token.anonymous && request.body.action !== 'framework.Auth/login') {
                            // INFO: user gets logged out and redirected but requests are not prevented
                            this.userService.logoutAutomatic(response.jwt_token.accessToken, response.jwt_token.refreshToken);
                        }

                        this.tokenSubject.next(response.jwt_token.accessToken);

                        // handle original request
                        return next.handle(this.addTokenToRequest(request, response.jwt_token.accessToken));
                    }
                }),
                catchError(_ => {
                    return this.userService.logout();
                }),
                finalize(() => {
                    this.isRefreshingToken = false;
                })
            );
        } else {
            this.isRefreshingToken = false;
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.addTokenToRequest(request, token));
                })
            );
        }
    }
}
